import type { SolutionEnvironmentsEnum } from '@pypestream/api-services';

import type { PlatformConnection } from './get-pype-connections';

/**
 * A voice channel escalating into a Contact Center VOICE INBOX needs its pype on
 * a connection of its own. Contact Center's availability check
 * (`/webhooks/pype/contactCenter/v1/voice/availability`) reads the inbox from
 * the connection's integration headers on the pype — not from the channel's
 * `voice_inbox_id` — so a voice pype left on the chat connection is looked up
 * in the chat inbox, 404s, and the call is hung up.
 */

const CONTACT_CENTER = 'ContactCenter';

const str = (v: unknown) => (v == null ? '' : String(v));

const headersOf = (c: PlatformConnection) => {
  const s = c.settings ?? {};
  const h = (s.integration_headers ?? {}) as Record<string, unknown>;
  return {
    accountId: str(h.account_id ?? s.account_id),
    inboxId: str(h.inbox_id ?? s.inbox_id),
  };
};

export const isContactCenterConnection = (c: PlatformConnection): boolean =>
  c.platformId === CONTACT_CENTER ||
  str(c.settings?.integrationName) === CONTACT_CENTER;

/** The Contact Center account the project's connections already point at, if any. */
export const contactCenterAccountOf = (
  connections: PlatformConnection[],
  env: SolutionEnvironmentsEnum
): string =>
  connections
    .filter((c) => isContactCenterConnection(c) && c.environment === env)
    .map((c) => headersOf(c).accountId)
    .find(Boolean) ?? '';

/** The settings blob a connection for one Contact Center inbox carries. */
export const voiceInboxConnectionSettings = (
  accountId: string,
  inboxId: string
) => {
  const headers = {
    inbox_id: inboxId,
    account_id: accountId,
    integrationName: CONTACT_CENTER,
  };
  return {
    ...headers,
    integration_name: CONTACT_CENTER,
    integration_headers: headers,
  };
};

export interface VoiceInboxTarget {
  pypeId: string;
  env: SolutionEnvironmentsEnum;
  accountId: string;
  inboxId: string;
}

export type VoiceInboxConnectionPlan =
  | { kind: 'no-template' }
  | {
      kind: 'ready';
      /** The connection to bind the pype to; null means create `create` first. */
      connectionId: string | null;
      create: {
        name: string;
        platformId: string;
        agentRouting: string;
        agentRoutingUrl: string | null;
        settings: ReturnType<typeof voiceInboxConnectionSettings>;
      } | null;
      /** Connections in the same environment the pype must leave. */
      detachFrom: string[];
      /** Already bound to the right connection and nothing else: no writes. */
      done: boolean;
    };

/**
 * What it takes to put a voice pype on the connection for its inbox: reuse a
 * connection already pointing at that account + inbox, or create one modeled on
 * the project's existing Contact Center connection (its routing and callback
 * URL), and leave every other connection in the environment.
 */
export const planVoiceInboxConnection = (
  connections: PlatformConnection[],
  target: VoiceInboxTarget
): VoiceInboxConnectionPlan => {
  const sameEnv = connections.filter((c) => c.environment === target.env);
  const isTarget = (c: PlatformConnection) => {
    const h = headersOf(c);
    return (
      isContactCenterConnection(c) &&
      h.accountId === target.accountId &&
      h.inboxId === target.inboxId
    );
  };
  const existing = sameEnv.find(isTarget);
  const template =
    sameEnv.find(
      (c) =>
        isContactCenterConnection(c) &&
        headersOf(c).accountId === target.accountId
    ) ?? sameEnv.find(isContactCenterConnection);

  if (!existing && !template) return { kind: 'no-template' };

  const hasPype = (c: PlatformConnection) =>
    c.pypes.some((p) => p.pypeId === target.pypeId && p.env === target.env);
  const detachFrom = sameEnv
    .filter((c) => c.id !== existing?.id && hasPype(c))
    .map((c) => c.id);

  return {
    kind: 'ready',
    connectionId: existing?.id ?? null,
    create: existing
      ? null
      : {
          name: `Contact Center voice inbox ${target.inboxId}`,
          platformId: template!.platformId || CONTACT_CENTER,
          agentRouting: template!.agentRouting,
          agentRoutingUrl: template!.agentRoutingUrl ?? null,
          settings: voiceInboxConnectionSettings(
            target.accountId,
            target.inboxId
          ),
        },
    detachFrom,
    done: !!existing && hasPype(existing) && detachFrom.length === 0,
  };
};
